import { useState, useCallback, useEffect } from 'react';
import { Lead } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { mapDbLeadToLead } from '@/services/leads/mappers/leadMapper';
import { LocationFilter } from '@/types/filters';
import { buildLocationFilterString } from '@/services/leads/queries/getLeadsPage';

interface AIFilterResult {
  filteredLeads: Lead[] | null;
  scores: Record<string, number>;
  isFiltering: boolean;
  isAIActive: boolean;
  mode: 'smart_match' | 'free_search' | null;
  smartMatch: () => Promise<void>;
  freeSearch: (query: string) => Promise<void>;
  clearAIFilter: () => void;
}

interface CompanyData {
  name: string;
  description?: string;
  industry?: string;
  country?: string;
  city?: string;
  website?: string;
}

interface LeadSummary {
  id: string;
  name: string;
  address: string | null;
  category: string | null;
  rating: number | null;
  reviewCount: number | null;
  status: string | null;
  website: string | null;
  email: any;
  phone: string | null;
  webSearchData: any;
  combinedData: any;
}

interface AILeadFilterOptions {
  locationFilter?: LocationFilter | null;
  selectedLocation?: string | null;
  selectedUserId?: string | null;
  currentUserId?: string | null;
}

export function useAILeadFilter(
  pageLeads: Lead[],
  companyId?: string,
  options?: AILeadFilterOptions
): AIFilterResult {
  const [filteredLeads, setFilteredLeads] = useState<Lead[] | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [isFiltering, setIsFiltering] = useState(false);
  const [mode, setMode] = useState<'smart_match' | 'free_search' | null>(null);
  const { toast } = useToast();

  // Sync filtered leads with latest pageLeads data (e.g. after starring)
  useEffect(() => {
    if (!filteredLeads) return;
    const leadMap = new Map(pageLeads.map(l => [l.id, l]));
    const synced = filteredLeads
      .map(l => leadMap.get(l.id) || l)
      .filter(Boolean) as Lead[];
    setFilteredLeads(synced);
  }, [pageLeads]);

  const fetchCompanyData = useCallback(async (): Promise<CompanyData | null> => {
    if (!companyId) return null;
    const { data } = await supabase
      .from('companies')
      .select('name, description, industry, country, city, website')
      .eq('id', companyId)
      .single();
    return data;
  }, [companyId]);

  // Fetch leads for AI - respects the active location filter
  const fetchFilteredLeadSummaries = useCallback(async (): Promise<LeadSummary[]> => {
    if (!companyId) return [];

    const allLeads: LeadSummary[] = [];
    const batchSize = 1000;
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      let query = supabase
        .from('leads')
        .select('id, name, address, category, rating, review_count, status, website, email, phone, web_search_data, combined_data')
        .eq('company_id', companyId);

      // Apply user filter so AI only processes the visible subset
      const selectedUserId = options?.selectedUserId;
      const currentUserId = options?.currentUserId;
      if (selectedUserId === 'my-leads' && currentUserId) {
        query = query.eq('created_by', currentUserId);
      } else if (selectedUserId && selectedUserId !== 'my-leads') {
        query = query.eq('created_by', selectedUserId);
      }

      // Apply location filter so AI only processes the subset
      const locFilter = buildLocationFilterString(options?.locationFilter, options?.selectedLocation);
      if (locFilter) {
        query = query.or(locFilter);
      }

      const { data, error } = await query.range(from, from + batchSize - 1);

      if (error) {
        console.error('[useAILeadFilter] Error fetching leads:', error);
        break;
      }

      if (data && data.length > 0) {
        allLeads.push(...data.map((l: any) => ({
          id: l.id,
          name: l.name,
          address: l.address,
          category: l.category,
          rating: l.rating,
          reviewCount: l.review_count,
          status: l.status,
          website: l.website,
          email: l.email,
          phone: l.phone,
          webSearchData: l.web_search_data,
          combinedData: l.combined_data,
        })));
        hasMore = data.length === batchSize;
        from += batchSize;
      } else {
        hasMore = false;
      }
    }

    return allLeads;
  }, [companyId, options?.locationFilter, options?.selectedLocation, options?.selectedUserId, options?.currentUserId]);

  // Fetch full lead data for matched IDs
  const fetchFullLeads = useCallback(async (leadIds: string[]): Promise<Lead[]> => {
    if (!leadIds.length || !companyId) return [];

    const results: Lead[] = [];
    for (let i = 0; i < leadIds.length; i += 50) {
      const batch = leadIds.slice(i, i + 50);
      const { data, error } = await supabase
        .from('leads')
        .select(`
          id, name, address, category, email, phone, website, image_url,
          is_favorite, rating, review_count, status, latitude, longitude,
          web_search_status, web_search_queue_time, web_search_data,
          web_search_updated_at, web_search_retry_count, web_search_last_error,
          combined_data, combined_updated_at,
          deep_research_status, deep_research_updated_at,
          crawl_status, crawl_updated_at,
          company_id, place_id, created_by, created_at, updated_at,
          notes:notes ( id, content, created_at, created_by, lead_id, tags, updated_at ),
          documents:documents ( id, name, url, type, description, lead_id, created_at, uploaded_by ),
          lead_images:lead_images ( id, image_url, is_primary, position, created_at ),
          lead_key_contacts:lead_key_contacts ( id, name, email, phone, position, is_primary, notes, created_at, created_by, linkedin_url, source )
        `)
        .in('id', batch);

      if (error) {
        console.error('[useAILeadFilter] Error fetching full leads:', error);
        continue;
      }

      if (data) {
        results.push(...data.map((lead: any) => {
          const leadObj = mapDbLeadToLead(lead);
          return {
            ...leadObj,
            images: (lead.lead_images || []).map((img: any) => ({
              id: img.id,
              leadId: lead.id,
              imageUrl: img.image_url,
              isPrimary: img.is_primary,
              position: img.position,
              createdAt: img.created_at,
            })),
            keyContacts: (lead.lead_key_contacts || []).map((contact: any) => ({
              id: contact.id,
              leadId: lead.id,
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
              position: contact.position,
              isPrimary: contact.is_primary,
              notes: contact.notes,
              createdAt: contact.created_at,
              createdBy: contact.created_by,
              linkedinUrl: contact.linkedin_url || undefined,
              source: contact.source || 'manual',
            })),
          };
        }));
      }
    }

    // Preserve AI-ranked order
    const leadMap = new Map(results.map(l => [l.id, l]));
    return leadIds.map(id => leadMap.get(id)).filter(Boolean) as Lead[];
  }, [companyId]);

  const summariseLeads = (leads: LeadSummary[]) =>
    leads.map((l) => {
      const webData = l.webSearchData as any;
      const combined = l.combinedData as any;
      return {
        id: l.id,
        name: l.name,
        address: l.address,
        category: l.category,
        rating: l.rating,
        reviewCount: l.reviewCount,
        status: l.status,
        website: l.website,
        email: typeof l.email === 'string' ? l.email : Array.isArray(l.email) ? l.email.map((e: any) => e.email).join(', ') : '',
        phone: l.phone || '',
        summary: webData?.summary?.slice(0, 2000) || '',
        services: webData?.services || [],
        products: webData?.products || [],
        locations: webData?.locations || [],
        industry: webData?.linkedIn?.industry || '',
        specialties: [],
        employeeCount: '',
        keyContacts: [],
        marketPosition: webData?.marketPosition || '',
        companySize: webData?.companySize || '',
        combinedSummary: combined?.summary?.slice(0, 1000) || '',
      };
    });

  const callFilter = useCallback(
    async (filterMode: 'smart_match' | 'free_search', query?: string) => {
      setIsFiltering(true);
      setMode(filterMode);
      try {
        // Fetch leads respecting current location filter
        const allLeads = await fetchFilteredLeadSummaries();

        if (!allLeads.length) {
          toast({ title: 'No leads', description: 'No leads match the current filters. Adjust your location or other filters first.' });
          setMode(null);
          return;
        }

        const companyData = await fetchCompanyData();
        if (!companyData) {
          toast({ variant: 'destructive', title: 'Error', description: 'Could not load company data.' });
          setMode(null);
          return;
        }

        const { data, error } = await supabase.functions.invoke('ai-filter-leads', {
          body: {
            leads: summariseLeads(allLeads),
            companyData,
            query: query || '',
            mode: filterMode,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        const leadIds: string[] = data.leadIds || [];

        // Fetch full lead data for matched IDs
        const fullLeads = await fetchFullLeads(leadIds);

        setFilteredLeads(fullLeads);
        setScores(data.scores || {});

        toast({
          title: filterMode === 'smart_match' ? 'Smart Match Complete' : 'AI Search Complete',
          description: `Found ${fullLeads.length} relevant lead${fullLeads.length !== 1 ? 's' : ''} out of ${allLeads.length} total.`,
        });
      } catch (err: any) {
        console.error('[useAILeadFilter]', err);
        toast({ variant: 'destructive', title: 'AI Filter Error', description: err.message || 'Something went wrong.' });
        setMode(null);
      } finally {
        setIsFiltering(false);
      }
    },
    [fetchFilteredLeadSummaries, fetchFullLeads, fetchCompanyData, toast]
  );

  const smartMatch = useCallback(() => callFilter('smart_match'), [callFilter]);
  const freeSearch = useCallback((query: string) => callFilter('free_search', query), [callFilter]);
  const clearAIFilter = useCallback(() => {
    setFilteredLeads(null);
    setScores({});
    setMode(null);
  }, []);

  return {
    filteredLeads,
    scores,
    isFiltering,
    isAIActive: filteredLeads !== null,
    mode,
    smartMatch,
    freeSearch,
    clearAIFilter,
  };
}
